import React, { useEffect, useState } from "react";
import { Field, Formik, Form, FieldArray, getIn } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import AppInput from "components/Input/AppInput";
import { Box, FormControlLabel, FormGroup, makeStyles, RadioGroup, Typography } from "@material-ui/core";
import { YupValidations } from "utils/validate";
import AppColor from "utilities/AppColor";
import AppButton from "components/CustomButtons/AppButton";
import SelectField from "components/Select/SelectField";
import CustomDatePicker from "components/Input/CustomDatePicker";
import AppCheckbox from "components/Input/AppCheckbox";
import MultiChoiceField from "components/Input/MultiChoiceField";
import { WORKER_TYPE } from "config/AppConfig";
import moment from "moment-timezone";
import {
  fetchAccreditation
} from "store/slices/worker";
import { useDispatch } from "react-redux";
import { WORK_STATUS } from "config/AppConfig";
import AppRadio from "components/Input/AppRadio";
import { FormFeedback } from "reactstrap";

const styles = makeStyles(() => ({
  container: {
    '& hr': {
      borderTop: '1px solid #00000080'
    }
  },
  title: {
    display: 'flex',
    margin: '1rem 0 0.5rem 0'
  },
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    '& a': {
      cursor: 'pointer',
      textDecoration: 'underline',
      color: AppColor.blue
    }
  },
  wrapEndDate: {
    position: 'relative',
    '& .float-checkbox': {
      position: 'absolute',
      top: '1.25rem',
      '& .MuiFormControlLabel-label': {
        fontSize: '0.8rem'
      }
    },
    '& label': {
      marginBottom: '2.75rem'
    }
  },
  deleteRight: {
    fontSize: '0.8rem',
    padding: '1rem 0',
    cursor: 'pointer',
    width: 'fit-content',
    marginLeft: 'auto'
  }
}));

const defaultWorkHistory = {
  company: '',
  jobTitle: '',
  startDate: '',
  endDate: '',
  isWorking: false
};

const maskNumber = '9999';

const ProfessionalInformationForm = (props) => {
  const {
    onSubmit,
    isSubmitting,
    specialties = [],
    languages = [],
    supports = [],
    workerDetail,
    changeProgressStatus,
    display,
    disabled,
    type
  } = props;

  const { t } = useTranslation();
  const classes = styles();
  const dispatch = useDispatch();

  const [accreditations, setAccreditations] = useState([]);

  const [initForm, setInitForm] = useState({
    accreditationId: '',
    languageIds: [],
    specialtyIds: [],
    supportIds: [],
    workHistoryRequests: [defaultWorkHistory],
    yearsOfExperience: null,
    workStatus: ''
  });

  const {
    id,
    accreditationId,
    workHistories = [],
    yearsOfExperience,
    workStatus,
    role: workerType = type
  } = workerDetail;

  const workingValidate = Yup.object().shape({
    jobTitle: Yup.string().when("status", (status) => {
      if (!status || status !== 'INACTIVE') {
        return YupValidations.requiredString;
      }
    }),
    company: Yup.string().when("status", (status) => {
      if (!status || status !== 'INACTIVE') {
        return YupValidations.requiredString;
      }
    }),
    startDate: Yup.date().when("status", (status) => {
      if (!status || status !== 'INACTIVE') {
        return Yup.date()
          .required("validation.required")
          .when("endDate", (endDate) => {
            if (endDate && endDate !== '') {
              return Yup.date()
                .required("validation.required")
                // .max(
                //   moment(endDate).format('yyyy/MM/01'),
                //   "validation.startDateMustBeLessThanEndDate"
                // )
              .when('isWorking', (isWorking) => {
                if (!isWorking) {
                  return Yup.date()
                    .required("validation.required")
                    .test('startDate', 'validation.startDateMustBeLessThanEndDate', startDate => {
                      if (moment(new Date(startDate).setHours(0,0,0,0)).diff(new Date(endDate).setHours(0,0,0,0)) > 0) {
                        return false;
                      } else {
                        return true;
                      }
                    })
                }
              })
            }
          });
      }
    }),
    endDate: Yup.date().when("status", (status) => {
      if (!status || status !== 'INACTIVE') {
        return Yup.date().when("isWorking", (isWorking) => {
          if (!isWorking) {
            return Yup.date()
              .required("validation.required")
              .when("startDate", (startDate) => {
                if (startDate && startDate !== '') {
                  return Yup.date()
                    .required("validation.required")
                    .min(
                      moment(startDate).format('yyyy/MM/01'),
                      "validation.endDateMustBeGreaterThanStartDate"
                    )
                }
              })
          }
        });
      }
    })
  },
    [
      ["endDate", "status", "isWorking", "startDate"],
      ["startDate", "status", "endDate"]
    ]
  );
  const validationSchema = Yup.object().shape({
    accreditationId: Yup.number().required("validation.required"),
    languageIds: Yup.array().required("validation.required"),
    specialtyIds: workerType === WORKER_TYPE.NURSE && Yup.array().required("validation.required"),
    supportIds: workerType === WORKER_TYPE.SUPPORT_WORKER && Yup.array().required("validation.required"),
    workHistoryRequests: Yup.array().of(workingValidate),
    yearsOfExperience: Yup.number().integer().nullable(true).test('len', "validation.max_length_10", val => val !== '' || Math.ceil(Math.log10(val + 1)) <= 10),
    workStatus: Yup.string().trim().required("validation.required")
  });

  useEffect(() => {
    if (workerDetail && Object.keys(workerDetail).length > 0) {
      const initial = {
        accreditationId,
        languageIds: workerDetail.languages.map(item => item.id),
        specialtyIds: workerDetail.specialties ? workerDetail.specialties.map(item => item.id) : [],
        supportIds: workerDetail.supports ? workerDetail.supports.map(item => item.id) : [],
        workHistoryRequests: [...workHistories.map(item => {
          const clone = { ...item };
          if (clone["endDate"] === null || clone["endDate"] === undefined) {
            clone["endDate"] = moment(new Date()).utc().format('yyyy/MM/01');
          }
          return clone;
        })],
        yearsOfExperience: yearsOfExperience === '' ? null : yearsOfExperience,
        workStatus: workStatus
      };
      setInitForm(initial);
      validationSchema
        .isValid(initial)
        .then((valid) => {
          setTimeout(() => {
            changeProgressStatus(1, valid);
          }, 200);
        });
    }
  }, [workerDetail]);

  useEffect(() => {
    if (workerType === WORKER_TYPE.SUPPORT_WORKER) {
      loadAccreditations(WORKER_TYPE.SUPPORT_WORKER);
    } else {
      loadAccreditations(WORKER_TYPE.NURSE);
    }
  }, [workerType]);

  const handleSubmit = (form) => {
    let params = { ...form };
    params.workHistoryRequests = params.workHistoryRequests.map(item => {
      let clone = { ...item }
      // clone['startDate'] = moment(item.startDate).utc().format('yyyy/MM/01');
      // clone['endDate'] = moment(item.endDate).utc().format('yyyy/MM/01');
      clone['startDate'] = moment(item.startDate).format('yyyy/MM/01');
      clone['endDate'] = moment(item.endDate).format('yyyy/MM/01');
      return clone;
    });
    onSubmit({ ...params, id });
  };
  const loadAccreditations = async (workerType) => {
    const result = await dispatch(fetchAccreditation({ workerType: workerType }));
    if (result.payload) {
      setAccreditations(result.payload);
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      onSubmit={handleSubmit}
      initialValues={initForm}
      validationSchema={validationSchema}
    >
      {({ values, setFieldTouched, handleChange, errors, touched }) => {
        return (
          <Form className={`${classes.container} ${!display && 'hidden'}`}>
            {
              workerType === WORKER_TYPE.SUPPORT_WORKER
                ? (
                  <React.Fragment>
                    <Box className={classes.title}>
                      <Typography variant="h5">
                        {t("title.supports")}
                        <span style={{ color: AppColor.red }}>*</span>
                      </Typography>
                    </Box>
                    <Field
                      component={MultiChoiceField}
                      name="supportIds"
                      options={supports}
                      minWidth={250}
                      disabled={disabled}
                    />
                  </React.Fragment>
                )
                : (
                  <React.Fragment>
                    <Box className={classes.title}>
                      <Typography variant="h5">
                        {t("title.specialties")}
                        <span style={{ color: AppColor.red }}>*</span>
                      </Typography>
                    </Box>
                    <Field
                      component={MultiChoiceField}
                      name="specialtyIds"
                      options={specialties}
                      disabled={disabled}
                    />
                  </React.Fragment>
                )
            }
            <hr />
            <Box className={classes.title}>
              <Typography variant="h5">
                {t("title.accreditation")}
                <span style={{ color: AppColor.red }}>*</span>
              </Typography>
            </Box>
            <Field
              component={SelectField}
              name="accreditationId"
              options={accreditations.map(item => {
                return {
                  value: item.id,
                  label: item.name
                }
              })}
              placeholder={t("label.accreditation")}
              disabled={disabled}
            />
            <hr />
            <Box className={classes.title}>
              <Typography variant="h5">
                {t("title.languages")}
                <span style={{ color: AppColor.red }}>*</span>
              </Typography>
            </Box>
            <Field
              component={MultiChoiceField}
              name="languageIds"
              options={languages}
              disabled={disabled}
            />
            <hr />
            <Box className={classes.title}>
              <Typography variant="h5">
                {t("title.yearsOfExperience")}
              </Typography>
            </Box>
            <Field
              component={AppInput}
              name="yearsOfExperience"
              type="tel"
              mask={maskNumber}
              maskChar=""
              disabled={disabled}
            />
            <hr />
            <Box className={classes.title}>
              <Typography variant="h5">
                {t("label.workStatus")}
                <span style={{ color: AppColor.red }}>*</span>
              </Typography>
            </Box>
            {getIn(errors, "workStatus") && getIn(touched, "workStatus") && (
              <FormFeedback style={{
                display: 'inline'
              }}>
                {
                  t(getIn(errors, "workStatus"))
                }
              </FormFeedback>
            )}
            <RadioGroup
              aria-label={t("label.workStatus")}
              name="workStatus"
              value={values.workStatus}
              onChange={handleChange("workStatus")}
              className={classes.radioGroup}
            >
              <FormControlLabel
                value={WORK_STATUS.AUSTRALIAN_CITIZEN}
                control={<AppRadio />}
                label={t(`workStatus.${WORK_STATUS.AUSTRALIAN_CITIZEN}`)}
                className={classes.radio}
              />
              <FormControlLabel
                value={WORK_STATUS.NEW_ZEALAND_CITIZEN}
                control={<AppRadio />}
                label={t(`workStatus.${WORK_STATUS.NEW_ZEALAND_CITIZEN}`)}
                className={classes.radio}
              />
              <FormControlLabel
                value={WORK_STATUS.AUSTRALIA_WORK_VISA}
                control={<AppRadio />}
                label={t(`workStatus.${WORK_STATUS.AUSTRALIA_WORK_VISA}`)}
                className={classes.radio}
              />
            </RadioGroup>
            <hr />
            <Box className={classes.title}>
              <Typography variant="h5">
                {t("title.workHistory")}
              </Typography>
            </Box>
            <Box className="mb-3">
              {t("text.enterWorkHistory")}
            </Box>
            <FieldArray
              name="workHistoryRequests"
              render={({ insert, remove, replace }) => (
                <React.Fragment>
                  {
                    values.workHistoryRequests && values.workHistoryRequests.map((item, index) => {
                      if (item.status && item.status === 'INACTIVE') {
                        return;
                      }
                      return (
                        <Box key={`ip-${index}`}>
                          <Field
                            component={AppInput}
                            name={`workHistoryRequests[${index}].jobTitle`}
                            label={"label.jobTitleRole"}
                            required
                            disabled={disabled}
                          />
                          <Field
                            component={AppInput}
                            name={`workHistoryRequests[${index}].company`}
                            label={"label.company"}
                            required
                            disabled={disabled}
                          />
                          <Field
                            component={CustomDatePicker}
                            name={`workHistoryRequests[${index}].startDate`}
                            label={"label.startDate"}
                            required
                            formatDateWorkHistory={true}
                            custom={{
                              format: "yyyy/MM",
                              openTo: "year",
                              views: ["year", "month"],
                              maxDate: new Date(moment(new Date()).startOf("day").add(1, "days"))
                            }}
                            disabled={disabled}
                            onChange={() => setFieldTouched(`workHistoryRequests[${index}].startDate`, true)}
                          />
                          <Box className={classes.wrapEndDate}>
                            <Field
                              component={AppCheckbox}
                              label={t("text.currentlyWorking")}
                              name={`workHistoryRequests[${index}].isWorking`}
                              className={'float-checkbox'}
                              disabled={disabled}
                            />
                            <Field
                              component={CustomDatePicker}
                              name={`workHistoryRequests[${index}].endDate`}
                              label={"label.endDate"}
                              required
                              formatDateWorkHistory={true}
                              disabled={values.workHistoryRequests[index].isWorking || disabled}
                              custom={{
                                format: "yyyy/MM",
                                openTo: "year",
                                views: ["year", "month"],
                                maxDate: new Date(moment(new Date()).startOf("day").add(1, "days"))
                              }}
                              onChange={() => setFieldTouched(`workHistoryRequests[${index}].endDate`, true)}
                            />
                          </Box>
                          {
                            values.workHistoryRequests.length > 0 && (
                              <Box
                                className={`text-right`}
                                style={{
                                  fontSize: '0.8rem',
                                  cursor: 'pointer'
                                }}
                              >
                                <Box
                                  onClick={() => {
                                    if (values.workHistoryRequests[index].id) {
                                      const newValue = {
                                        ...values.workHistoryRequests[index]
                                      };

                                      newValue.status = 'INACTIVE';
                                      replace(index, newValue);
                                    } else {
                                      remove(index);
                                    }

                                  }}
                                  className={`${classes.deleteRight} ${disabled ? 'disabled' : ''}`}
                                >
                                  <i className="far fa-trash-alt mr-2"></i>
                                  {t('text.deleteJob')}
                                </Box>
                              </Box>
                            )
                          }
                        </Box>
                      );
                    })
                  }
                  <Box className="mb-3">
                    <AppButton
                      className="btn mr-1"
                      mode="outlined"
                      buttonColor={AppColor.mainBlue}
                      type="button"
                      disabled={disabled}
                      label={t('button.addWorkHistory')}
                      onClick={() => {
                        insert(values.workHistoryRequests.length, { ...defaultWorkHistory });
                      }}
                    >
                      <i className="fas fa-plus"></i>
                    </AppButton>
                  </Box>
                  <hr />
                </React.Fragment >
              )}
            />
            {
              !disabled && (
                <FormGroup className={classes.control}>
                  <AppButton
                    label={`${t("button.saveAndContinue")}`}
                    rounder
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  />
                </FormGroup>
              )
            }
          </Form>
        );
      }}
    </Formik>
  );
};

ProfessionalInformationForm.propTypes = {
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  display: PropTypes.bool,
  disabled: PropTypes.bool,
  specialties: PropTypes.array,
  languages: PropTypes.array,
  supports: PropTypes.array,
  workerType: PropTypes.string,
  workerDetail: PropTypes.any,
  type: PropTypes.string,
  changeProgressStatus: PropTypes.func
};

export default ProfessionalInformationForm;
